/**
 * @file killSwitch.ts
 * @description The single global kill-switch for UI-catalog localization.
 * @module i18n
 */
import { UI_LOCALIZATION } from "@/config/featureFlags";

/**
 * Build-time environment override for the UI-localization kill-switch.
 *
 * `"0" | "false" | "off"` forces the switch off; `"1" | "true" | "on"` forces it
 * on; anything else (including unset) defers to the static `UI_LOCALIZATION`
 * flag. Read through a helper rather than at module scope so a test can flip
 * `process.env` between cases.
 * @returns The override, or undefined when the env var is unset/unrecognized
 */
function readEnvOverride(): boolean | undefined {
  const raw = process.env.EXPO_PUBLIC_I18N_ENABLED;
  if (raw === undefined) return undefined;
  const normalized = raw.trim().toLowerCase();
  if (normalized === "0" || normalized === "false" || normalized === "off") {
    return false;
  }
  if (normalized === "1" || normalized === "true" || normalized === "on") {
    return true;
  }
  return undefined;
}

/**
 * Whether UI-catalog localization is enabled.
 *
 * This is the ONE global switch for the i18n runtime (SE-6507). It is a
 * different flag from the auto-translate flag that gates Bedrock call volume —
 * they gate different things and their enablement dates are independent.
 * @returns true when non-English catalogs may be selected
 */
export function isUiLocalizationEnabled(): boolean {
  return readEnvOverride() ?? UI_LOCALIZATION === "on";
}
